/**
 * Date Utilities for Seeding Calculator
 * Handles parsing of frost dates and date range calculations
 */

/**
 * Parses frost date string and returns a Date object
 * Examples: "October 1-15" → October 15 (if useLatest=true)
 *          "April 15-May 1" → May 1 (if useLatest=true)
 *          "April 15" → April 15
 * 
 * @param dateString - Frost date string from regional climate data
 * @param year - Year to use for the date
 * @param useLatest - If true, use the latest date in range (conservative approach)
 * @returns Date object
 */
export function parseFrostDate(
  dateString: string,
  year: number,
  useLatest: boolean = true
): Date {
  // Handle "rare" or other special cases
  if (dateString.toLowerCase() === 'rare' || !dateString) {
    // Return a default date far in future/past
    return new Date(year, useLatest ? 11 : 0, useLatest ? 31 : 1)
  }

  // Month name to number mapping
  const months: Record<string, number> = {
    'january': 0, 'february': 1, 'march': 2, 'april': 3,
    'may': 4, 'june': 5, 'july': 6, 'august': 7,
    'september': 8, 'october': 9, 'november': 10, 'december': 11
  }

  // Check if date range spans two months (e.g., "April 15-May 1")
  const crossMonthMatch = dateString.match(/(\w+)\s+(\d+)-(\w+)\s+(\d+)/)
  if (crossMonthMatch) {
    const [, month1, day1, month2, day2] = crossMonthMatch
    const monthNum1 = months[month1.toLowerCase()]
    const monthNum2 = months[month2.toLowerCase()]
    
    if (useLatest) {
      return new Date(year, monthNum2, parseInt(day2))
    } else {
      return new Date(year, monthNum1, parseInt(day1))
    }
  }

  // Check if date range within same month (e.g., "October 1-15")
  const sameMonthMatch = dateString.match(/(\w+)\s+(\d+)-(\d+)/)
  if (sameMonthMatch) {
    const [, month, day1, day2] = sameMonthMatch
    const monthNum = months[month.toLowerCase()]
    const day = useLatest ? parseInt(day2) : parseInt(day1)
    return new Date(year, monthNum, day)
  }

  // Single date (e.g., "April 15")
  const singleDateMatch = dateString.match(/(\w+)\s+(\d+)/)
  if (singleDateMatch) {
    const [, month, day] = singleDateMatch
    const monthNum = months[month.toLowerCase()]
    return new Date(year, monthNum, parseInt(day))
  }

  // Fallback - return mid-year date
  return new Date(year, 6, 1)
}

/**
 * Gets the current growing year
 * If after September 1, use next year for spring dates to handle year boundaries
 * @returns Year number
 */
export function getCurrentGrowingYear(): number {
  const now = new Date()
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth()
  
  // If we're in Sept-Dec, next year's spring is more relevant
  if (currentMonth >= 8) {
    return currentYear + 1
  }
  
  return currentYear
}

/**
 * Adds or subtracts days from a date
 * @param date - Starting date
 * @param days - Number of days to add (positive) or subtract (negative)
 * @returns New Date object
 */
export function addDays(date: Date, days: number): Date {
  const result = new Date(date)
  result.setDate(result.getDate() + days)
  return result
}

/**
 * Formats a date range for display
 * @param start - Start date
 * @param end - End date
 * @returns Formatted string like "August 15 - September 15, 2025"
 */
export function formatDateRange(start: Date, end: Date): string {
  const startMonth = start.toLocaleDateString('en-US', { month: 'long' })
  const startDay = start.getDate()
  const endMonth = end.toLocaleDateString('en-US', { month: 'long' })
  const endDay = end.getDate()
  const year = end.getFullYear()

  if (start.getMonth() === end.getMonth()) {
    return `${startMonth} ${startDay}-${endDay}, ${year}`
  } else {
    return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`
  }
}

/**
 * Checks if a date falls within a date range (inclusive)
 * @param date - Date to check
 * @param start - Range start
 * @param end - Range end
 * @returns True if date is within range
 */
export function isDateInRange(date: Date, start: Date, end: Date): boolean {
  const time = date.getTime()
  return time >= start.getTime() && time <= end.getTime()
}

/**
 * Formats a single date for display
 * @param date - Date to format
 * @returns Formatted string like "October 15, 2025"
 */
export function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', { 
    month: 'long', 
    day: 'numeric', 
    year: 'numeric' 
  })
}

/**
 * Gets the number of days between two dates
 * @param start - Start date
 * @param end - End date
 * @returns Number of days
 */
export function daysBetween(start: Date, end: Date): number {
  const msPerDay = 1000 * 60 * 60 * 24
  const startTime = start.getTime()
  const endTime = end.getTime()
  return Math.round((endTime - startTime) / msPerDay)
}

/**
 * Determines which seeding window a date falls into
 * @param date - Date to check
 * @param optimalStart - Start of optimal window
 * @param optimalEnd - End of optimal window
 * @param acceptableEnd - End of acceptable window
 * @returns Window status
 */
export function getWindowStatus(
  date: Date,
  optimalStart: Date,
  optimalEnd: Date,
  acceptableEnd: Date
): 'before' | 'optimal' | 'acceptable' | 'too_late' {
  if (date < optimalStart) return 'before'
  if (isDateInRange(date, optimalStart, optimalEnd)) return 'optimal'
  if (isDateInRange(date, optimalEnd, acceptableEnd)) return 'acceptable'
  return 'too_late'
}

/**
 * Gets the current season based on the current date
 * @returns Current season
 */
export function getCurrentSeason(): 'spring' | 'summer' | 'fall' | 'winter' {
  const now = new Date()
  const month = now.getMonth() + 1 // getMonth() returns 0-11

  if (month >= 3 && month <= 5) return 'spring'
  if (month >= 6 && month <= 8) return 'summer'
  if (month >= 9 && month <= 11) return 'fall'
  return 'winter'
}

/**
 * Parses peak growth periods from strings like "March-May, September-November"
 * Returns array of date ranges for the growing year
 * @param peakGrowthString - Peak growth period string from grass characteristics
 * @param baseYear - Base year for date calculations (defaults to current growing year)
 * @returns Array of date range objects with start and end dates
 */
export function parsePeakGrowthPeriods(
  peakGrowthString: string,
  baseYear: number = getCurrentGrowingYear()
): Array<{ start: Date; end: Date }> {
  if (!peakGrowthString) return []

  const months: Record<string, number> = {
    'january': 0, 'february': 1, 'march': 2, 'april': 3,
    'may': 4, 'june': 5, 'july': 6, 'august': 7,
    'september': 8, 'october': 9, 'november': 10, 'december': 11
  }

  const periods: Array<{ start: Date; end: Date }> = []

  // Split by comma for multiple periods (e.g., "March-May, September-November")
  const periodStrings = peakGrowthString.split(',').map(s => s.trim())

  for (const periodStr of periodStrings) {
    // Match pattern like "March-May" or "September-November"
    const match = periodStr.match(/(\w+)-(\w+)/)
    if (match) {
      const [, startMonth, endMonth] = match
      const startMonthNum = months[startMonth.toLowerCase()]
      const endMonthNum = months[endMonth.toLowerCase()]

      if (startMonthNum !== undefined && endMonthNum !== undefined) {
        // Use first day of start month and last day of end month
        const start = new Date(baseYear, startMonthNum, 1)
        const end = new Date(baseYear, endMonthNum + 1, 0) // 0 = last day of previous month

        periods.push({ start, end })
      }
    }
  }

  return periods
}

